/**
 * Per-label-type default printer for the Print page.
 *
 * Which printer a label should come out of is a property of where the operator
 * is standing, not of the account: the counter machine wants the counter label
 * printer downstairs, the bench machine the workshop one upstairs. So the
 * defaults live in this browser's localStorage — setting one at the counter
 * doesn't redirect anybody else's labels. There is one print agent behind all
 * of them; the choice only picks which lane the job is addressed to.
 *
 * A stored default is a `{ siteId, role }` pair rather than a Dataverse row id,
 * because that is what the print envelope actually addresses (`siteId:role`) —
 * so a default survives the fleet row being deleted and recreated. A type with
 * no default stored means "let sm4rt-api resolve its configured default", which
 * is what every print did before this existed.
 */
import type { LabelKind } from '../utils/buildPrintLabelRequest';

/** The printer lane a job is addressed to. */
export interface PrinterTarget {
  siteId: string;
  role: string;
}

export type PrinterDefaults = Partial<Record<LabelKind, PrinterTarget>>;

const STORAGE_KEY = 'print_default_printers_v1';

/** Stable key for a lane — used as the dropdown option value. */
export function printerTargetKey(target: PrinterTarget): string {
  return `${target.siteId}::${target.role}`;
}

/** Parse a `printerTargetKey` back into a lane; null when malformed. */
export function parsePrinterTargetKey(key: string | null): PrinterTarget | null {
  if (!key) return null;
  const index = key.indexOf('::');
  if (index <= 0) return null;
  const siteId = key.slice(0, index);
  const role = key.slice(index + 2);
  return siteId && role ? { siteId, role } : null;
}

export function samePrinterTarget(
  a: PrinterTarget | null | undefined,
  b: PrinterTarget | null | undefined,
): boolean {
  if (!a || !b) return a === b || (!a && !b);
  return a.siteId === b.siteId && a.role === b.role;
}

function isTarget(value: unknown): value is PrinterTarget {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as PrinterTarget).siteId === 'string' &&
    typeof (value as PrinterTarget).role === 'string' &&
    Boolean((value as PrinterTarget).siteId) &&
    Boolean((value as PrinterTarget).role)
  );
}

/**
 * Read the stored defaults. Anything unparseable or of the wrong shape is
 * dropped rather than thrown — a corrupt preference must never stop the page
 * printing, it just falls back to the site default.
 */
export function loadPrinterDefaults(): PrinterDefaults {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    return {};
  }
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return {};
    const result: PrinterDefaults = {};
    for (const [kind, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (isTarget(value)) result[kind as LabelKind] = { siteId: value.siteId, role: value.role };
    }
    return result;
  } catch {
    return {};
  }
}

/**
 * Store (or, with `target = null`, clear) the default for one label type and
 * return the resulting map. Writes are best-effort — a full or unavailable
 * localStorage leaves the in-memory result usable for this session.
 */
export function savePrinterDefault(
  kind: LabelKind,
  target: PrinterTarget | null,
): PrinterDefaults {
  const next = loadPrinterDefaults();
  if (target) next[kind] = { siteId: target.siteId, role: target.role };
  else delete next[kind];
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // localStorage full or unavailable — the choice still applies this session.
  }
  return next;
}
